import { ColumnDef, Row } from '@tanstack/react-table';
import { VirtualItem } from '@tanstack/react-virtual';

import { useEmbeddedTableRows } from '../hooks/useEmbeddedTableRows/useEmbeddedTableRows';
import { useVisibleRows } from '../hooks/useVisibleRows/useVisibleRows';
import { DataRenderModes } from '../Types';
import { TableRow } from './TableRow/TableRow';

type VirtualizationConfig = {
  before: number;
  after: number;
  virtualRows: Array<VirtualItem>;
};
type TableBodyProps<TData, TValue> = {
  dataRenderMode: DataRenderModes;
  virtualizationConfig: VirtualizationConfig;
  columns: Array<ColumnDef<TData, TValue>>;
  showCellBorders: boolean;
  columnsWidth: string;
  rows: Array<Row<TData>>;
  parentColumnPaths: Array<string>;
  enableEmbeddedRowActions: boolean;
};

export function TableBody<TData, TValue>({
  columnsWidth,
  showCellBorders,
  dataRenderMode,
  virtualizationConfig,
  columns,
  rows,
  parentColumnPaths,
  enableEmbeddedRowActions,
}: TableBodyProps<TData, TValue>) {
  const { before, after, virtualRows } = virtualizationConfig;
  const colSpan = columns.length;

  const visibleRows = useVisibleRows({
    rows,
    virtualRows,
    isVirtualized: dataRenderMode === DataRenderModes.virtualized,
  });

  const rowsWithEmbeddedTableRowsInfo = useEmbeddedTableRows({
    rows: visibleRows,
    columns,
    parentColumnPaths,
    enableEmbeddedRowActions,
  });

  return (
    <tbody>
      {dataRenderMode === DataRenderModes.virtualized ? (
        <>
          {before > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: before }} />
            </tr>
          )}
          {rowsWithEmbeddedTableRowsInfo.map((rowWithEmbeddedTableRowInfo, index) => {
            return (
              <TableRow
                key={`${rowWithEmbeddedTableRowInfo.row.id}-${rowWithEmbeddedTableRowInfo.embeddedTableRowIndex}`}
                row={rowWithEmbeddedTableRowInfo.row}
                rowIndex={index}
                parentRowIndex={rowWithEmbeddedTableRowInfo.row.index}
                totalRows={rowsWithEmbeddedTableRowsInfo.length}
                showCellBorders={showCellBorders}
                columnsWidth={columnsWidth}
                rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
              />
            );
          })}
          {after > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: after }} />
            </tr>
          )}
        </>
      ) : (
        rowsWithEmbeddedTableRowsInfo.map((rowWithEmbeddedTableRowInfo, index) => (
          <TableRow
            key={`${rowWithEmbeddedTableRowInfo.row.id}-${rowWithEmbeddedTableRowInfo.embeddedTableRowIndex}`}
            row={rowWithEmbeddedTableRowInfo.row}
            rowIndex={index}
            parentRowIndex={rowWithEmbeddedTableRowInfo.row.index}
            totalRows={rowsWithEmbeddedTableRowsInfo.length}
            showCellBorders={showCellBorders}
            columnsWidth={columnsWidth}
            rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
          />
        ))
      )}
    </tbody>
  );
}
